import { type IllustrationType } from "@clipboard-health/ui-components";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";

import { type WorkerOutcomeResult } from "../../api/useUpdateInterview";
import { FEEDBACK_STEP, type FeedbackStep } from "./feedbackStep";
import { FreeTextStepContent } from "./FreeTextStepContent";
import { InterviewAttendanceConfirmationContent } from "./InterviewAttendanceConfirmationContent";
import { ListWorkplaceInterviewFeedbackContent } from "./ListWorkplaceInterviewFeedbackContent";
import {
  type FreeTextContext,
  GOT_OFFER_SUB_OPTIONS,
  NOT_HEARD_BACK_SUB_OPTIONS,
  NOT_INTERVIEW_OPTIONS,
  NOT_OFFERED_ROLE_OPTIONS,
} from "./optionConstants";
import { OptionSelectionStepContent } from "./OptionSelectionStepContent";
import { WorkplaceInterviewFeedbackContent } from "./WorkplaceInterviewFeedbackContent";

const CONFIRMATION_CONFIGS: Record<
  string,
  { title: string; description: string; illustration?: IllustrationType; toJobsPath?: boolean }
> = {
  [FEEDBACK_STEP.gotOffer]: {
    title: "Thanks for letting us know",
    description: "We'll use this to improve communication after interviews.",
  },
  [FEEDBACK_STEP.notHeardBack]: {
    title: "Thanks for letting us know",
    description: "We'll check back with you within a week.",
    toJobsPath: true,
  },
  [FEEDBACK_STEP.notOfferConfirmation]: {
    title: "Thanks for letting us know",
    description: "We're sorry it didn't work out this time.",
    toJobsPath: true,
  },
  [FEEDBACK_STEP.notInterviewConfirmation]: {
    title: "Thanks for letting us know",
    description: "We're working to prevent this from happening again.",
    toJobsPath: true,
  },
};

const OPTION_STEP_CONFIGS: Record<
  string,
  {
    options: Array<{
      label: string;
      value: WorkerOutcomeResult;
      textFieldHeading?: string;
      textFieldPlaceholder?: string;
    }>;
    heading: string;
    subtitle: string | undefined;
    nextStep: FeedbackStep;
    freeTextConfirmationStep?: FeedbackStep;
  }
> = {
  [FEEDBACK_STEP.gotOfferSubOptions]: {
    options: GOT_OFFER_SUB_OPTIONS,
    heading: "Did they explain what happens next?",
    subtitle: undefined,
    nextStep: FEEDBACK_STEP.gotOffer,
  },
  [FEEDBACK_STEP.notHeardBackSubOptions]: {
    options: NOT_HEARD_BACK_SUB_OPTIONS,
    heading: "When are they following up?",
    subtitle: undefined,
    nextStep: FEEDBACK_STEP.notHeardBack,
  },
  [FEEDBACK_STEP.notOfferTheRole]: {
    options: NOT_OFFERED_ROLE_OPTIONS,
    heading: "Why weren't you offered the role?",
    subtitle: undefined,
    nextStep: FEEDBACK_STEP.notOfferConfirmation,
    freeTextConfirmationStep: FEEDBACK_STEP.notOfferConfirmation,
  },
  [FEEDBACK_STEP.notInterview]: {
    options: NOT_INTERVIEW_OPTIONS,
    heading: "Why didn't you interview?",
    subtitle: "Please share the reason you did not interview.",
    nextStep: FEEDBACK_STEP.notInterviewConfirmation,
    freeTextConfirmationStep: FEEDBACK_STEP.notInterviewConfirmation,
  },
};

interface InterviewAttendanceFeedbackStepContentProps {
  feedbackStep: FeedbackStep | undefined;
  activeInterview: InterviewWithDetails | undefined;
  interviews: InterviewWithDetails[] | undefined;
  placementCandidate: PlacementCandidate;
  freeTextContext: FreeTextContext | undefined;
  hasPendingInterview: boolean | undefined;
  isFetchingInterviews: boolean;
  isGetInterviewsSuccess: boolean;
  isFetchingInterviewsNextPage: boolean;
  hasNextPageInterview: boolean | undefined;
  refetchInterviews: () => Promise<unknown>;
  fetchNextPageInterviews: () => Promise<unknown>;
  onCtaClicked: (toJobsPath?: boolean) => void;
  onFreeTextNeeded: (
    parentStep: FeedbackStep,
    confirmationStep: FeedbackStep,
    result: WorkerOutcomeResult,
    textFieldConfig: { heading: string; placeholder?: string }
  ) => void;
  onFeedbackStepChange: (step: FeedbackStep | undefined) => void;
  onFreeTextContextClear: () => void;
  onInterviewSelected: (interview: InterviewWithDetails) => void;
  onAllSetDismiss: () => void;
}

export function InterviewAttendanceFeedbackStepContent(
  props: InterviewAttendanceFeedbackStepContentProps
) {
  const {
    feedbackStep,
    activeInterview,
    interviews,
    placementCandidate,
    freeTextContext,
    hasPendingInterview,
    isFetchingInterviews,
    isGetInterviewsSuccess,
    isFetchingInterviewsNextPage,
    hasNextPageInterview,
    refetchInterviews,
    fetchNextPageInterviews,
    onCtaClicked,
    onFreeTextNeeded,
    onFeedbackStepChange,
    onFreeTextContextClear,
    onInterviewSelected,
    onAllSetDismiss,
  } = props;

  if (feedbackStep) {
    const confirmationConfig = CONFIRMATION_CONFIGS[feedbackStep];
    if (confirmationConfig) {
      let ctaText = "Done";
      if (hasPendingInterview) {
        ctaText = "Share next outcome";
      } else if (confirmationConfig.toJobsPath) {
        ctaText = "Keep browsing jobs";
      }
      return (
        <InterviewAttendanceConfirmationContent
          step={feedbackStep}
          placementCandidate={placementCandidate}
          interview={activeInterview}
          title={confirmationConfig.title}
          description={confirmationConfig.description}
          illustration={confirmationConfig.illustration}
          ctaText={ctaText}
          ctaOnClick={() => {
            onCtaClicked(confirmationConfig.toJobsPath);
          }}
        />
      );
    }

    const optionConfig = OPTION_STEP_CONFIGS[feedbackStep];
    if (optionConfig && activeInterview) {
      return (
        <OptionSelectionStepContent
          options={optionConfig.options}
          heading={optionConfig.heading}
          subtitle={optionConfig.subtitle}
          interview={activeInterview}
          step={feedbackStep}
          placementCandidate={placementCandidate}
          refetchInterviews={refetchInterviews}
          isFetchingInterviews={isFetchingInterviews}
          onSubmit={() => {
            onFeedbackStepChange(optionConfig.nextStep);
          }}
          onFreeTextNeeded={
            optionConfig.freeTextConfirmationStep
              ? (result, textFieldHeading, textFieldPlaceholder) => {
                  onFreeTextNeeded(feedbackStep, optionConfig.freeTextConfirmationStep!, result, {
                    heading: textFieldHeading,
                    placeholder: textFieldPlaceholder,
                  });
                }
              : undefined
          }
        />
      );
    }

    if (feedbackStep === FEEDBACK_STEP.freeText && freeTextContext && activeInterview) {
      return (
        <FreeTextStepContent
          interview={activeInterview}
          step={feedbackStep}
          placementCandidate={placementCandidate}
          result={freeTextContext.result}
          textFieldHeading={freeTextContext.textFieldHeading}
          textFieldPlaceholder={freeTextContext.textFieldPlaceholder}
          refetchInterviews={refetchInterviews}
          isFetchingInterviews={isFetchingInterviews}
          onSubmit={() => {
            onFeedbackStepChange(freeTextContext.confirmationStep);
            onFreeTextContextClear();
          }}
        />
      );
    }
  }

  if (interviews?.length === 0 && isGetInterviewsSuccess) {
    return (
      <InterviewAttendanceConfirmationContent
        placementCandidate={placementCandidate}
        title="You're all set for now"
        description="We'll prompt you here after your next interview so you can confirm the result."
        ctaText="Keep browsing jobs"
        ctaOnClick={onAllSetDismiss}
      />
    );
  }

  if (!activeInterview || !interviews) {
    return (
      <ListWorkplaceInterviewFeedbackContent
        interviews={interviews ?? []}
        isFetchingNextPageInterviews={isFetchingInterviewsNextPage}
        placementCandidate={placementCandidate}
        fetchNextPageInterviews={fetchNextPageInterviews}
        hasNextPageInterview={hasNextPageInterview}
        onInterviewSelected={onInterviewSelected}
      />
    );
  }

  return (
    <WorkplaceInterviewFeedbackContent
      interview={activeInterview}
      placementCandidate={placementCandidate}
      isFetchingInterviews={isFetchingInterviews}
      onOptionSelected={(step) => {
        onFeedbackStepChange(step);
      }}
    />
  );
}
